import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

import { formatFieldDisplayValue, getAdornment } from 'utils/formFieldDisplay';

import { Wrapper, Container, Heading } from './SubmissionAnswers.styled';
import SubmissionAnswersProps from './SubmissionAnswersProps';

import { FormAnswerGroupItem, FormAnswersGroup } from '../../types/types';

const SubmissionAnswers = ({ form, id }: SubmissionAnswersProps) => {
    const { watch } = useFormContext();
    const stepComponents = useMemo(() => ['FormStepBlock', 'FormResultStepBlock', 'FormSummaryStepBlock'], []);

    const [values, setValues] = useState<FieldValues>();
    const [answerGroups, setAnswerGroups] = useState([] as FormAnswersGroup[]);

    const groupAnswers = useCallback(
        (values: FieldValues): FormAnswersGroup[] => {
            const filterForm = () => {
                const filteredForm = [];
                for (const item of form) {
                    if (item.component === 'FormSummaryStepBlock') {
                        break;
                    }
                    filteredForm.push(item);
                }
                return filteredForm.filter((item) => item.elementName.startsWith('__field_'));
            };

            const groupedForm: FormAnswersGroup[] = [];
            let currentGroup: FormAnswerGroupItem[] = [];
            let groupLabel = '';

            filterForm().forEach((item) => {
                if (stepComponents.includes(item.component!)) {
                    if (currentGroup.length > 0) {
                        groupedForm.push({
                            label: groupLabel,
                            answers: currentGroup,
                        });
                        currentGroup = [];
                    }
                    groupLabel = item.label;
                } else {
                    const value = formatFieldDisplayValue(item, values);
                    const adornment = getAdornment(item);
                    if (value !== undefined) {
                        currentGroup.push({
                            label: item.label,
                            value,
                            adornment,
                        });
                    }
                }
            });

            if (currentGroup.length > 0) {
                groupedForm.push({
                    label: groupLabel,
                    answers: currentGroup,
                });
            }

            return groupedForm;
        },
        [form, stepComponents],
    );

    useEffect(() => {
        const subscription = watch((value) => {
            setValues(value);
        });

        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        if (values !== undefined) {
            setAnswerGroups(groupAnswers(values));
        }
    }, [values, groupAnswers]);

    return (
        <Wrapper>
            {answerGroups.map((group, index) => (
                <Container key={`submission-answer-${index}`}>
                    {group.label && <Heading id={`heading-${id}-${index}`}>{group.label}</Heading>}
                    <dl aria-labelledby={group.label ? `heading-${id}-${index}` : undefined}>
                        {group.answers.map((item, itemIndex) => (
                            <React.Fragment key={`answer-${itemIndex}`}>
                                <dt>{item.label}</dt>
                                <dd>
                                    {item.value} {item.adornment}
                                </dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </Container>
            ))}
        </Wrapper>
    );
};

export default memo(SubmissionAnswers);
